import { z } from 'zod';
import {
  BgvStatus,
  DeploymentStatus,
  EducationLevel,
  ExitReason,
  MedicalStatus,
  OfferStatus,
  RequirementStatus,
  ShiftType,
} from '../enums';
import { dateSchema, paginationSchema, zEnum } from './common';

/**
 * A client's manpower indent. This is the manpower-supply view of demand and
 * is deliberately separate from a published Job: one requirement may be served
 * by several job postings, or by direct sourcing with no posting at all.
 */
export const requirementSchema = z
  .object({
    companyId: z.string().uuid(),
    locationId: z.string().uuid().optional(),
    departmentId: z.string().uuid().optional(),
    title: z.string().trim().min(3).max(150),
    /** Head-count the client asked for. */
    quantity: z.coerce.number().int().min(1).max(10_000),
    minQualification: zEnum(EducationLevel).default(EducationLevel.TENTH),
    acceptedQualifications: z.array(zEnum(EducationLevel)).max(9).default([]),
    preferredTrades: z.array(z.string().trim().max(120)).max(20).default([]),
    minExperienceMonths: z.coerce.number().int().min(0).max(600).default(0),
    shift: zEnum(ShiftType).default(ShiftType.GENERAL),
    salaryMin: z.coerce.number().min(0).max(10_000_000),
    salaryMax: z.coerce.number().min(0).max(10_000_000),
    /** What we bill the client per head, per month. */
    billingRate: z.coerce.number().min(0).max(10_000_000).optional(),
    serviceChargePercent: z.coerce.number().min(0).max(100).optional(),
    targetDate: dateSchema.optional(),
    priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'CRITICAL']).default('NORMAL'),
    notes: z.string().trim().max(2000).optional().or(z.literal('')),
    /** Auto-create a published job posting from this requirement. */
    createJobPosting: z.boolean().default(false),
  })
  .refine((v) => v.salaryMax >= v.salaryMin, {
    message: 'Maximum salary cannot be less than minimum',
    path: ['salaryMax'],
  });
export type RequirementInput = z.infer<typeof requirementSchema>;

export const updateRequirementStatusSchema = z.object({
  status: zEnum(RequirementStatus),
  notes: z.string().trim().max(1000).optional(),
});

export const requirementSearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  companyId: z.string().uuid().optional(),
  status: zEnum(RequirementStatus).optional(),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'CRITICAL']).optional(),
  targetFrom: dateSchema.optional(),
  targetTo: dateSchema.optional(),
});
export type RequirementSearchQuery = z.infer<typeof requirementSearchSchema>;

// --- Screening -------------------------------------------------------------

export const screeningSchema = z.object({
  applicationId: z.string().uuid(),
  /** Phone screening outcome captured by the recruitment desk. */
  contactedAt: dateSchema.optional(),
  isReachable: z.boolean().default(true),
  isInterested: z.boolean().default(true),
  expectedSalary: z.coerce.number().min(0).max(10_000_000).optional(),
  availableFrom: dateSchema.optional(),
  documentsComplete: z.boolean().default(false),
  score: z.coerce.number().int().min(0).max(100).optional(),
  remarks: z.string().trim().max(2000).optional().or(z.literal('')),
  /** Push to SHORTLISTED (true) or REJECTED (false); omit to leave in SCREENING. */
  shortlist: z.boolean().optional(),
});
export type ScreeningInput = z.infer<typeof screeningSchema>;

// --- Medical ---------------------------------------------------------------

export const medicalRecordSchema = z.object({
  applicationId: z.string().uuid(),
  hospitalName: z.string().trim().max(200).optional().or(z.literal('')),
  examDate: dateSchema.optional(),
  status: zEnum(MedicalStatus).default(MedicalStatus.PENDING),
  /** Common shop-floor screens: vision, colour blindness, audiometry. */
  visionResult: z.string().trim().max(100).optional().or(z.literal('')),
  colourBlindness: z.boolean().optional(),
  bloodGroup: z.string().trim().max(10).optional().or(z.literal('')),
  heightCm: z.coerce.number().min(100).max(250).optional(),
  weightKg: z.coerce.number().min(25).max(250).optional(),
  reportDocumentId: z.string().uuid().optional(),
  remarks: z.string().trim().max(1000).optional().or(z.literal('')),
  cost: z.coerce.number().min(0).max(1_000_000).optional(),
});
export type MedicalRecordInput = z.infer<typeof medicalRecordSchema>;

// --- Background verification ----------------------------------------------

export const bgvSchema = z.object({
  applicationId: z.string().uuid(),
  agencyName: z.string().trim().max(200).optional().or(z.literal('')),
  referenceNumber: z.string().trim().max(80).optional().or(z.literal('')),
  initiatedAt: dateSchema.optional(),
  completedAt: dateSchema.optional(),
  status: zEnum(BgvStatus).default(BgvStatus.PENDING),
  addressVerified: z.boolean().optional(),
  identityVerified: z.boolean().optional(),
  educationVerified: z.boolean().optional(),
  employmentVerified: z.boolean().optional(),
  criminalRecordFound: z.boolean().optional(),
  reportDocumentId: z.string().uuid().optional(),
  remarks: z.string().trim().max(2000).optional().or(z.literal('')),
});
export type BgvInput = z.infer<typeof bgvSchema>;

// --- Offers ----------------------------------------------------------------

export const offerSchema = z
  .object({
    applicationId: z.string().uuid(),
    designation: z.string().trim().min(2).max(150),
    joiningDate: dateSchema,
    /** Monthly gross; the breakup below must sum to it. */
    grossSalary: z.coerce.number().min(0).max(10_000_000),
    basic: z.coerce.number().min(0).optional(),
    hra: z.coerce.number().min(0).optional(),
    conveyance: z.coerce.number().min(0).optional(),
    otherAllowance: z.coerce.number().min(0).optional(),
    /** Employee-side statutory deductions, shown on the offer for transparency. */
    pfDeduction: z.coerce.number().min(0).optional(),
    esiDeduction: z.coerce.number().min(0).optional(),
    probationMonths: z.coerce.number().int().min(0).max(24).default(6),
    bondMonths: z.coerce.number().int().min(0).max(60).default(0),
    reportingTo: z.string().trim().max(150).optional().or(z.literal('')),
    reportingLocationId: z.string().uuid().optional(),
    validUntil: dateSchema.optional(),
    terms: z.string().trim().max(8000).optional().or(z.literal('')),
    notifyCandidate: z.boolean().default(true),
  })
  .refine(
    (v) => {
      const parts = [v.basic, v.hra, v.conveyance, v.otherAllowance].filter(
        (n): n is number => typeof n === 'number',
      );
      if (parts.length === 0) return true;
      const sum = parts.reduce((a, b) => a + b, 0);
      // Allow a rupee of rounding slack.
      return Math.abs(sum - v.grossSalary) <= 1;
    },
    { message: 'Salary breakup must add up to the gross salary', path: ['basic'] },
  );
export type OfferInput = z.infer<typeof offerSchema>;

export const offerResponseSchema = z.object({
  status: z.enum([OfferStatus.ACCEPTED, OfferStatus.DECLINED]),
  reason: z.string().trim().max(500).optional(),
});
export type OfferResponseInput = z.infer<typeof offerResponseSchema>;

// --- Joining & deployment --------------------------------------------------

export const deploymentSchema = z.object({
  applicationId: z.string().uuid(),
  locationId: z.string().uuid(),
  departmentId: z.string().uuid().optional(),
  employeeCode: z.string().trim().max(50).optional().or(z.literal('')),
  designation: z.string().trim().max(150).optional().or(z.literal('')),
  joinedAt: dateSchema,
  shift: zEnum(ShiftType).default(ShiftType.GENERAL),
  supervisorName: z.string().trim().max(120).optional().or(z.literal('')),
  supervisorPhone: z.string().trim().max(20).optional().or(z.literal('')),
  monthlySalary: z.coerce.number().min(0).max(10_000_000).optional(),
  /** Client-side commercials — phase 2 billing reads these. */
  billingRate: z.coerce.number().min(0).max(10_000_000).optional(),
  serviceChargePercent: z.coerce.number().min(0).max(100).optional(),
  /** Set when this person backfills someone who left. */
  replacesDeploymentId: z.string().uuid().optional(),
  notes: z.string().trim().max(1000).optional().or(z.literal('')),
});
export type DeploymentInput = z.infer<typeof deploymentSchema>;

export const exitDeploymentSchema = z.object({
  status: z.enum([
    DeploymentStatus.ON_NOTICE,
    DeploymentStatus.EXITED,
    DeploymentStatus.ABSCONDED,
    DeploymentStatus.TERMINATED,
  ]),
  exitDate: dateSchema.optional(),
  lastWorkingDate: dateSchema.optional(),
  exitReason: zEnum(ExitReason),
  remarks: z.string().trim().max(1000).optional().or(z.literal('')),
  /** Open a replacement requirement against the client automatically. */
  raiseReplacement: z.boolean().default(false),
});
export type ExitDeploymentInput = z.infer<typeof exitDeploymentSchema>;

export const transferDeploymentSchema = z.object({
  toLocationId: z.string().uuid(),
  toDepartmentId: z.string().uuid().optional(),
  effectiveDate: dateSchema,
  reason: z.string().trim().max(500).optional().or(z.literal('')),
  newSalary: z.coerce.number().min(0).max(10_000_000).optional(),
});
export type TransferDeploymentInput = z.infer<typeof transferDeploymentSchema>;

export const deploymentSearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  companyId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  status: zEnum(DeploymentStatus).optional(),
  joinedFrom: dateSchema.optional(),
  joinedTo: dateSchema.optional(),
});
export type DeploymentSearchQuery = z.infer<typeof deploymentSearchSchema>;
